import { useInfiniteQuery } from '@tanstack/react-query'
import { Empty, Flex, Select, SelectProps, Spin } from 'antd'
import { useState } from 'react'

import { useTemplatesServiceGetSystemTemplateListEndpointKey } from 'src/api/queries'
import {
  ContentType,
  TemplateItemDto,
  TemplatesService
} from 'src/api/requests'
import { useDebounce } from 'src/hooks'
import { useLocales } from 'src/locales'

export const TreatmentTemplatesInfiniteSelect = (props: SelectProps) => {
  const { t } = useLocales()
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search)

  const { data, isFetching, fetchNextPage, hasNextPage } = useInfiniteQuery({
    queryKey: [
      useTemplatesServiceGetSystemTemplateListEndpointKey,
      debouncedSearch
    ],
    queryFn: async ({ pageParam = 1 }: { pageParam?: number }) => {
      const response = await TemplatesService.getSystemTemplateListEndpoint(
        pageParam,
        50,
        debouncedSearch,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        [ContentType.CONSULTATION]
      )

      return {
        data: response.data,
        total_pages: response.totalPages ?? 0,
        current_page: pageParam
      }
    },
    getNextPageParam: (lastPage: {
      data?: TemplateItemDto[]
      total_pages: number
      current_page: number
    }) =>
      lastPage?.current_page < lastPage?.total_pages
        ? lastPage.current_page + 1
        : undefined,
    initialPageParam: 1
  })

  const options = [
    ...(data?.pages.flatMap(
      (page) =>
        page.data?.map((item) => ({
          value: item?.id,
          label: item?.name
        })) || []
    ) ?? [])
  ]

  return (
    <Select
      allowClear
      popupMatchSelectWidth={true}
      filterOption={false}
      options={options}
      onSearch={(value) => setSearch(value)}
      onClear={() => setSearch('')}
      loading={isFetching}
      placeholder={t('templates.treatment_templates')}
      showSearch={true}
      onPopupScroll={(e) => {
        const target = e.target as HTMLElement
        if (target.scrollTop + target.offsetHeight === target.scrollHeight) {
          hasNextPage && fetchNextPage()
        }
      }}
      notFoundContent={
        isFetching ? (
          <Flex justify="center">
            <Spin />
          </Flex>
        ) : (
          <Empty />
        )
      }
      {...props}
    />
  )
}
